import { useEffect, useMemo, useState } from "react";
import type { Skill } from "../services/api";
import { createSkill, deleteSkill, listSkills, updateSkill } from "../services/api";
import type { AlertState } from "../utils/errors";
import { getErrorAlertMessage } from "../utils/errors";
import { Modal } from "./Modal";
import { SkillForm, type SkillFormValues, toFormValues } from "./SkillForm";

export function SkillsPanel() {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [groupFilter, setGroupFilter] = useState<string>("");
  const [tagFilter, setTagFilter] = useState<string>("");
  const [certFilter, setCertFilter] = useState<string>("");
  const [relatedFilter, setRelatedFilter] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    void refreshSkills();
  }, []);

  async function refreshSkills() {
    try {
      const data = await listSkills();
      setSkills(data);
      setAlert((current) => (current?.type === "error" ? null : current));
    } catch (err) {
      setAlert({ type: "error", message: getErrorAlertMessage("load skills", err) });
    }
  }

  function openCreateModal() {
    setEditingSkill(null);
    setIsModalOpen(true);
  }

  function openEditModal(skill: Skill) {
    setEditingSkill(skill);
    setIsModalOpen(true);
  }

  function closeModal() {
    setEditingSkill(null);
    setIsModalOpen(false);
  }

  async function handleDelete(skill: Skill) {
    if (!window.confirm(`Delete skill ${skill.name}?`)) {
      return;
    }
    try {
      await deleteSkill(skill.skill_code);
      await refreshSkills();
      setAlert({ type: "info", message: "Skill deleted." });
    } catch (err) {
      setAlert({ type: "error", message: getErrorAlertMessage("delete this skill", err) });
    }
  }

  async function handleSave(values: SkillFormValues) {
    const payload: Skill = {
      ...values,
      last_reviewed_utc: values.last_reviewed_utc || undefined,
    };

    setIsSaving(true);
    setAlert(null);

    try {
      if (editingSkill) {
        const { skill_code: _skillCode, ...updates } = payload;
        void _skillCode;
        await updateSkill(editingSkill.skill_code, updates);
        setAlert({ type: "info", message: "Skill updated successfully." });
      } else {
        await createSkill(payload);
        setAlert({ type: "info", message: "Skill created successfully." });
      }
      await refreshSkills();
      closeModal();
    } catch (err) {
      setAlert({ type: "error", message: getErrorAlertMessage("save this skill", err) });
    } finally {
      setIsSaving(false);
    }
  }

  const filteredSkills = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return skills.filter((skill) => {
      if (groupFilter && (skill.group_type ?? "UNCLASSIFIED") !== groupFilter) {
        return false;
      }
      if (!query) return true;
      const haystack = [
        skill.skill_code,
        skill.name,
        skill.description,
        (skill.tags ?? []).join(" "),
        (skill.certs ?? []).join(" "),
      ]
        .join(" ")
        .toLowerCase();
      const matchesQuery = haystack.includes(query);
      const matchesTag = !tagFilter || (skill.tags ?? []).includes(tagFilter);
      const matchesCert = !certFilter || (skill.certs ?? []).includes(certFilter);
      const matchesRelated = !relatedFilter || (skill.related_skills ?? []).includes(relatedFilter);
      return matchesQuery && matchesTag && matchesCert && matchesRelated;
    });
  }, [skills, groupFilter, searchTerm, tagFilter, certFilter, relatedFilter]);

  const groupedSkills = useMemo(() => {
    const groups = new Map<string, Skill[]>();
    filteredSkills.forEach((skill) => {
      const key = skill.group_type ?? "UNCLASSIFIED";
      const list = groups.get(key) ?? [];
      list.push(skill);
      groups.set(key, list);
    });
    const safeName = (skill: Skill) => skill.name ?? skill.skill_code;
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([group, entries]) => ({
        group,
        items: entries.sort((a, b) => safeName(a).localeCompare(safeName(b))),
      }));
  }, [filteredSkills]);

  const groupOptions = useMemo(() => {
    const unique = new Set<string>();
    skills.forEach((skill) => unique.add(skill.group_type ?? "UNCLASSIFIED"));
    return Array.from(unique).sort();
  }, [skills]);

  const tagOptions = useMemo(() => {
    const unique = new Set<string>();
    skills.forEach((skill) => (skill.tags ?? []).forEach((tag) => unique.add(tag)));
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [skills]);

  const certOptions = useMemo(() => {
    const unique = new Set<string>();
    skills.forEach((skill) => (skill.certs ?? []).forEach((cert) => unique.add(cert)));
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [skills]);

  const relatedSkillOptions = useMemo(() => {
    const unique = new Set<string>();
    skills.forEach((skill) => (skill.related_skills ?? []).forEach((code) => unique.add(code)));
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [skills]);

  return (
    <div className="layout single-column">
      <div className="panel skills-catalog">
        <div className="panel-header">
          <h2>Skills Catalog</h2>
          <div className="filters">
            <button type="button" className="primary" onClick={openCreateModal}>
              Add Skill
            </button>
            <input
              type="search"
              placeholder="Search by name, code, description, or tag"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <select value={groupFilter} onChange={(event) => setGroupFilter(event.target.value)}>
              <option value="">All groups</option>
              {groupOptions.map((group) => (
                <option key={group} value={group}>
                  {group}
                </option>
              ))}
            </select>
            <select value={tagFilter} onChange={(event) => setTagFilter(event.target.value)}>
              <option value="">All tags</option>
              {tagOptions.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
            <select value={certFilter} onChange={(event) => setCertFilter(event.target.value)}>
              <option value="">All certifications</option>
              {certOptions.map((cert) => (
                <option key={cert} value={cert}>
                  {cert}
                </option>
              ))}
            </select>
            <select value={relatedFilter} onChange={(event) => setRelatedFilter(event.target.value)}>
              <option value="">All related skills</option>
              {relatedSkillOptions.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
            <button type="button" className="ghost" onClick={() => void refreshSkills()}>
              Refresh
            </button>
          </div>
        </div>
        {alert && (
          <div className={`alert ${alert.type}`}>
            {alert.message}
            {alert.details && alert.details.length > 0 && (
              <ul>
                {alert.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {groupedSkills.length === 0 ? (
          <div className="empty">No skills match the filters. Try broadening your search.</div>
        ) : (
          groupedSkills.map(({ group, items }) => (
            <section key={group} className="skill-group">
              <header className="skill-group-header">
                <h3>{group}</h3>
                <span>{items.length} skill{items.length === 1 ? "" : "s"}</span>
              </header>
              <div className="skill-grid">
                {items.map((skill) => (
                  <article key={skill.skill_code} className="skill-card">
                    <header>
                      <h4>{skill.name}</h4>
                      <span className="skill-code">{skill.skill_code}</span>
                    </header>
                    {skill.description && <p className="skill-description">{skill.description}</p>}
                    <div className="skill-meta">
                      {skill.tags && skill.tags.length > 0 && (
                        <div className="meta-block">
                          <span className="meta-label">Tags</span>
                          <div className="chip-row">
                            {skill.tags.map((tag) => (
                              <span key={tag} className="chip">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {skill.certs && skill.certs.length > 0 && (
                        <div className="meta-block">
                          <span className="meta-label">Certifications</span>
                          <div className="chip-row">
                            {skill.certs.map((cert) => (
                              <span key={cert} className="chip accent">
                                {cert}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {skill.related_skills && skill.related_skills.length > 0 && (
                        <div className="meta-block">
                          <span className="meta-label">Related Skills</span>
                          <div className="chip-row">
                            {skill.related_skills.map((related) => (
                              <span key={related} className="chip muted">
                                {related}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <footer className="skill-actions">
                      <button type="button" onClick={() => openEditModal(skill)}>
                        Edit
                      </button>
                      <button type="button" className="danger" onClick={() => void handleDelete(skill)}>
                        Delete
                      </button>
                    </footer>
                  </article>
                ))}
              </div>
            </section>
          ))
        )}
      </div>
      <Modal open={isModalOpen} onClose={closeModal} title={editingSkill ? "Edit Skill" : "Add Skill"}>
        <SkillForm
          initial={editingSkill ? toFormValues(editingSkill) : undefined}
          isSaving={isSaving}
          onSubmit={(values) => void handleSave(values)}
          onCancel={closeModal}
        />
      </Modal>
    </div>
  );
}
